import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useState } from "react";
import { useTheme } from "@/context/ThemeContext";

const expectations = [
  { num: "01", text: "Review your current process" },
  { num: "02", text: "Identify inefficiencies" },
  { num: "03", text: "Map automation opportunities" },
  { num: "04", text: "Recommend a system tailored to your business" },
];

const fits = [
  "Generate consistent leads",
  "Have a service-based business",
  "Want to scale without adding overhead",
  "Are ready to implement real systems",
];

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    setLoading(true);
    try {
      await fetch("https://readdy.ai/api/form/d7c1638r4h7u93gnq0vg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = `w-full border rounded-xl px-4 py-3 text-sm transition-colors focus:outline-none ${
    isDark
      ? "bg-white/5 border-white/10 text-white placeholder-white/25 focus:border-white/30"
      : "bg-white border-black/10 text-[#1d1d1f] placeholder-[#6e6e73]/50 focus:border-black/25"
  }`;

  const labelClass = `text-[10px] tracking-widest uppercase font-semibold block mb-2 ${isDark ? "text-white/40" : "text-[#6e6e73]"}`;

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? "bg-[#0a0a0a]" : "bg-white"}`}>
      <Navbar />
      <main>
        <section className="pt-40 pb-16 text-center px-6">
          <p className={`text-[11px] tracking-[0.15em] uppercase font-semibold mb-6 ${isDark ? "text-white/35" : "text-[#6e6e73]"}`}>Let&apos;s Talk</p>
          <h1 className={`text-4xl md:text-6xl font-black max-w-3xl mx-auto leading-tight tracking-[-0.02em] mb-6 ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>
            Let&apos;s Build Your Complete AI System
          </h1>
          <p className={`text-lg max-w-2xl mx-auto leading-relaxed font-light ${isDark ? "text-white/50" : "text-[#6e6e73]"}`}>
            If your business is losing leads, missing follow-ups, or relying on manual processes — it&apos;s not a marketing issue. It&apos;s a system issue.
          </p>
        </section>

        <section className="max-w-7xl mx-auto px-6 md:px-10 pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div className="flex flex-col gap-5">
              <div className={`rounded-2xl border p-8 ${isDark ? "bg-[#111111] border-white/6" : "bg-[#f5f5f7] border-black/5"}`}>
                <h2 className={`font-black text-xl mb-6 ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>On your strategy call:</h2>
                <div className="flex flex-col gap-4">
                  {expectations.map((item) => (
                    <div key={item.num} className="flex items-center gap-4">
                      <span className={`font-black text-sm w-8 flex-shrink-0 ${isDark ? "text-white/20" : "text-[#1d1d1f]/20"}`}>{item.num}</span>
                      <span className={`text-base ${isDark ? "text-white/70" : "text-[#1d1d1f]/80"}`}>{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className={`rounded-2xl border p-8 ${isDark ? "bg-[#111111] border-white/6" : "bg-[#f5f5f7] border-black/5"}`}>
                <h2 className={`font-black text-xl mb-6 ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>You&apos;re a fit if you:</h2>
                <div className="flex flex-col gap-3">
                  {fits.map((fit) => (
                    <div key={fit} className="flex items-center gap-3">
                      <i className="ri-checkbox-circle-fill text-emerald-400 text-lg flex-shrink-0"></i>
                      <span className={`text-base ${isDark ? "text-white/70" : "text-[#1d1d1f]/80"}`}>{fit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className={`rounded-2xl border p-8 md:p-10 ${isDark ? "bg-[#111111] border-white/6" : "bg-[#f5f5f7] border-black/5"}`}>
              {submitted ? (
                <div className="flex flex-col items-center justify-center h-full text-center py-16">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-500/15 mb-6">
                    <i className="ri-check-line text-3xl text-emerald-400"></i>
                  </div>
                  <h3 className={`font-black text-2xl mb-3 ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>You&apos;re booked in!</h3>
                  <p className={`text-base ${isDark ? "text-white/50" : "text-[#6e6e73]"}`}>We&apos;ll be in touch within 24 hours to confirm your strategy call.</p>
                </div>
              ) : (
                <>
                  <h2 className={`font-black text-2xl mb-8 ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>Book Your AI Strategy Call</h2>
                  <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className={labelClass}>First Name</label>
                        <input name="first_name" type="text" required placeholder="John" className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>Last Name</label>
                        <input name="last_name" type="text" required placeholder="Smith" className={inputClass} />
                      </div>
                    </div>
                    <div>
                      <label className={labelClass}>Email</label>
                      <input name="email" type="email" required placeholder="john@company.com" className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Phone Number</label>
                      <input name="phone" type="tel" placeholder="+1 (555) 000-0000" className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Business Type</label>
                      <select name="business_type" required className={`${inputClass} cursor-pointer`}>
                        <option value="" className={isDark ? "bg-[#1a1a1a]" : "bg-white"}>Select your industry</option>
                        <option value="Home Services" className={isDark ? "bg-[#1a1a1a]" : "bg-white"}>Home Services</option>
                        <option value="Legal Services" className={isDark ? "bg-[#1a1a1a]" : "bg-white"}>Legal Services</option>
                        <option value="Med Spa & Wellness" className={isDark ? "bg-[#1a1a1a]" : "bg-white"}>Med Spa &amp; Wellness</option>
                        <option value="Insurance" className={isDark ? "bg-[#1a1a1a]" : "bg-white"}>Insurance</option>
                        <option value="Healthcare" className={isDark ? "bg-[#1a1a1a]" : "bg-white"}>Healthcare</option>
                        <option value="Other" className={isDark ? "bg-[#1a1a1a]" : "bg-white"}>Other</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>What&apos;s your biggest challenge?</label>
                      <textarea name="challenge" rows={4} maxLength={500} placeholder="Describe your current situation..." className={`${inputClass} resize-none`} />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className={`w-full py-4 font-bold text-base rounded-xl transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-60 ${
                        isDark ? "bg-white text-[#0a0a0a] hover:bg-white/90" : "bg-[#1d1d1f] text-white hover:bg-[#1d1d1f]/85"
                      }`}
                    >
                      {loading ? "Submitting..." : "Book Your AI Strategy Call"}
                    </button>
                    <p className={`text-xs text-center ${isDark ? "text-white/25" : "text-[#6e6e73]/60"}`}>No commitment. We&apos;ll review your situation and recommend the right system.</p>
                  </form>
                </>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
